import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { downloadBlob } from '../../../../../modules/refunds/utils/documentUtils';

interface DrgHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const DrgHelpDialog: React.FC<DrgHelpDialogProps> = ({ open, onClose }) => {
  const handleDownloadTemplate = () => {
    const headers = ['Drg', 'Description', 'Weight', 'Alos', 'Pointer'];
    const csvContent = headers.join(',');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, 'drg_template.csv');
  };

  const csvColumns = [
    { field: 'Drg', description: 'DRG code (numeric, will be zero-padded to 3 digits)', example: '1' },
    { field: 'Description', description: 'DRG title/description', example: 'Heart Transplant' },
    { field: 'Weight', description: 'DRG weight (decimal number with up to 4 decimal places)', example: '26.1234' },
    { field: 'Alos', description: 'Arithmetic Mean Length of Stay (decimal number)', example: '23.4' },
    { field: 'Pointer', description: 'Grouper pointer/code', example: 'A' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>DRG Management Help</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              About DRGs
            </Typography>
            <Typography variant="body2" paragraph>
              Diagnosis Related Groups (DRGs) are a patient classification system that standardizes prospective payment 
              to hospitals and encourages cost containment initiatives. DRGs are used to classify hospital cases into 
              groups expected to have similar hospital resource use.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              CSV Upload Format
            </Typography>
            <Typography variant="body2" paragraph>
              Upload a CSV file with the following columns. The first row should contain column headers:
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Column Name</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Example</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvColumns.map((column) => (
                    <TableRow key={column.field}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.field}
                      </TableCell>
                      <TableCell>{column.description}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {column.example}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Import Behavior:</strong> Uploading a CSV file will replace ALL existing DRG codes in this fee schedule.
              Make sure to include all DRGs you want to keep in your CSV file.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="h6" gutterBottom>
              Field Details
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Drg:</strong> Must be a numeric code. The system will automatically zero-pad to 3 digits (e.g., 1 becomes 001).
              <Typography variant="body2" paragraph>
                <strong>Description:</strong> Title or descriptive name of the DRG classification.
              </Typography>
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Weight:</strong> Enter as decimal number with up to 4 decimal places (e.g., 26.1234).
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Alos:</strong> Arithmetic Mean Length of Stay as a decimal number representing average days.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Pointer:</strong> Single character grouper pointer/code used for classification purposes.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace', 
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre>{`Drg,Description,Weight,Alos,Pointer
1,Heart Transplant,26.1234,23.4,A
2,Kidney Transplant,13.5678,18.2,B
23,Craniotomy,4.7890,12.8,C`}</pre>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          size="small"
        >
          Download Template
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrgHelpDialog;

